import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {map} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ConfigService, DcpLoggingService, FeatureTogglesService} from '@dcp/configurator';
import {fromEvent} from 'rxjs';
import {DcpAuthService} from '@app/auth/services/dcp-auth.service';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';

declare global {
  interface Window {
    uidBaselineSettings: any;
    baseline: {
      signOutCallback: () => void;
      signOut: () => void;
      signInCallback: () => void;
      signIn: () => void;
    };
  }
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DcpBaselineAuthService {
  isBaselineSSOEnabled = false;

  constructor(
    @Inject(PLATFORM_ID) public platformId: string,
    protected authService: DcpAuthService,
    protected loggingService: DcpLoggingService,
    protected featureTogglesService: FeatureTogglesService,
    protected configService: ConfigService
  ) {
    if (isPlatformBrowser(this.platformId)) {
      window.uidBaselineSettings = this.configService.getObject('baselineUid');
      featureTogglesService
        .isEnabled(UNLEASH_TOGGLES.baseLineSSO)
        .pipe(untilDestroyed(this))
        .subscribe((enabled) => {
          this.isBaselineSSOEnabled = enabled;
          if (enabled) {
            fromEvent(window, 'validUidSession')
              .pipe(untilDestroyed(this))
              .subscribe((event) => this.onStartUidSession(event));
            fromEvent(window, 'startUidSession')
              .pipe(untilDestroyed(this))
              .subscribe((event) => this.onStartUidSession(event));
          }
        });
    }
  }

  login() {
    return this.authService.isUserLoggedIn().pipe(
      untilDestroyed(this),
      map((isUserLoggedIn) => {
        if (!isUserLoggedIn) {
          this.authService.login();
        }
      })
    );
  }

  logout(): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      if (this.isBaselineSSOEnabled) {
        return this.authService
          .coreLogout()
          .then(() => window.baseline?.signOut())
          .catch((error) => {
            console.error('Error during DcpBaselineAuthService logout with isBaselineSSO Enabled:', error);
          });
      } else {
        return this.authService
          .coreLogout()
          .then(() => {})
          .catch((error) => {
            console.error('Error during DcpBaselineAuthService logout with isBaselineSSO Disabled:', error);
          });
      }
    }
  }

  onStartUidSession(event: Event): void {
    this.login().pipe(untilDestroyed(this)).subscribe();
  }
}
